import { useQsAdminApiManager } from '@/auth/api/qsadminapiManager'
import { analisi, personalizzazione, azienda } from '@/model/qsadminapi/QsAdminApiModuleModel'
import { RoleRouteObject, RouteFunctionParams } from '@/types'
import { redirect } from 'react-router-dom'
import { LoaderFunction, ActionFunction, defer } from 'react-router-typesafe'
import { getAzureUserInformation } from '@/auth/azure/azureManager'
import { Roles } from '@/auth/azure/Roles'
import { Qanalisi, Qpersonalizzazione } from '@/model/qsadminapi/QQsAdminApiModule'
import { Reparto } from '@/enums'
import CustomizationEditor from '@/routes/customizations/CustomizationEditor'
import { ODataModelResponseV4 } from '@odata2ts/odata-core'
import { HttpResponseModel, ODataClientError } from '@odata2ts/http-client-api'

export const customizationEditorRouteLoader = (async ({
  request,
  params,
}: RouteFunctionParams<'customizationId' | 'clientId'>) => {
  const customizationId = params.customizationId
  const clientId = params.clientId
  const analysisId = new URL(request.url).searchParams.get('analysisId')

  const qsAdminApi = useQsAdminApiManager.getState().service

  let azienda: HttpResponseModel<ODataModelResponseV4<azienda>> | undefined
  if (clientId)
    azienda = await qsAdminApi.azienda(Number(clientId)).query((builder, azienda) => {
      builder.expanding('gruppo_aziendale', (gruppo_aziendaleBuilder, gruppo_aziendale) => {
        gruppo_aziendaleBuilder.select('id', 'nome')
      })
      builder.select('id', 'nome', 'gruppo_aziendale')
    })

  let title: string = ``
  let customization: personalizzazione

  if (customizationId) {
    //editor
    const getCustomization = await qsAdminApi
      .personalizzazione(Number(customizationId))
      .query((builder, customization) => {
        builder.expanding('sede', (sedeBuilder, sede) => {
          sedeBuilder.expanding('azienda', (aziendaBuilder, azienda) => {
            aziendaBuilder.expanding('sedi', (sediBuilder, sedeAzienda) => {
              sediBuilder.select('id', 'nome', 'note')
              sediBuilder.orderBy(sedeAzienda.principale.desc())
            })
            aziendaBuilder.expanding('gruppo_aziendale', (gruppoAziendaleBuilder, gruppo_aziendale) => {
              gruppoAziendaleBuilder.select('id', 'nome')
            })
            aziendaBuilder.select('id', 'nome', 'sedi', 'gruppo_aziendale')
          })
          sedeBuilder.select('id', 'nome', 'azienda', 'note')
        })
        builder.expanding('responsabili', (responsabiliBuilder, responsabile) => {
          responsabiliBuilder.select('id', 'fullname')
          responsabiliBuilder.orderBy(responsabile.fullname.asc())
        })
        builder.expanding('analisis', (analisiBuilder, analisi) => {
          analisiBuilder.select('id', 'ded_Dis', 'oggetto')
        })
      })
      .catch((error: ODataClientError) => {
        if (error.status === 404) throw new Error('404 Not Found: Personalizzazione non trovata')
        throw new Error()
      })
    customization = getCustomization.data satisfies personalizzazione
    title = clientId ? `${customization.sede?.azienda?.nome} - ` : ``
    title += `${customization.ded_Dis}`
    if (clientId && customization?.sede?.azienda && customization?.sede?.azienda.id !== Number(clientId)) {
      throw new Error(
        `La personalizzazione ${customization.ded_Dis} appartiene al cliente ${customization?.sede?.azienda.nome} e non al cliente corrente`,
      )
    }
  } else {
    //creator
    title += `Nuova personalizzazione`

    //creator a partire da analisi quidi recupero analisi
    const analysisData = analysisId
      ? await qsAdminApi.analisi(Number(analysisId)).query((builder, analysis) => {
          builder.select('id', 'ded_Dis', 'oggetto')
        })
      : undefined

    title += analysisId ? ` da analisi ${analysisData?.data.ded_Dis}` : ``

    const sedeData = analysisData?.data
      ? await qsAdminApi.sede().query((sedeBuilder, sede) => {
          if (analysisData?.data)
            sedeBuilder.filter(sede.analisi.any((analisi: Qanalisi) => analisi.id.eq(analysisData?.data.id)))
          sedeBuilder.expanding('azienda', (aziendaSedeBuilder, aziendaSede) => {
            aziendaSedeBuilder.expanding('sedi', (sediAziendaSedeBuilder, sedeAzienda) => {
              sediAziendaSedeBuilder.select('id', 'nome', 'note')
              sediAziendaSedeBuilder.orderBy(sedeAzienda.principale.desc())
            })
            aziendaSedeBuilder.expanding('gruppo_aziendale', (gruppoAziendaleBuilder, gruppo_aziendale) => {
              gruppoAziendaleBuilder.select('id', 'nome')
            })
            aziendaSedeBuilder.select('id', 'nome', 'sedi', 'gruppo_aziendale')
          })
          sedeBuilder.select('id', 'nome', 'azienda', 'note')
        })
      : undefined

    customization = {
      id: 0,
      ded_RootFam: '',
      ded_Dis: '',
      titolo: null,
      note: null,
      sede: sedeData?.data.value[0] ?? null,
      supporto_sviluppo: true,
      attachmentsFolderUrl: '',
      analisis: analysisData ? [analysisData.data] : [],
    }
  }

  const getAziende = qsAdminApi.azienda().query((builder, azienda) => {
    builder.filter(azienda.sedi.any())
    if (analysisId)
      builder.filter(
        customization.sede?.azienda?.gruppo_aziendale
          ? azienda.gruppo_aziendale.props.id.eq(Number(customization.sede.azienda.gruppo_aziendale.id))
          : customization.sede
            ? azienda.id.eq(Number(customization.sede.azienda?.id))
            : null,
      )
    else builder.filter(clientId ? azienda.id.eq(Number(clientId)) : null)
    builder.orderBy(azienda.nome.asc())
    builder.select('id', 'nome')
  })

  const getQsImpiegati = qsAdminApi.user().query((builder, impiegato) => {
    builder.filter(
      impiegato.attivo
        .eq(true)
        .and(impiegato.azienda.props.p_iva.eq('01955220346'))
        .and(impiegato.reparto.props.id.eq(Reparto.SVILUPPO).or(impiegato.reparto.props.id.eq(Reparto.TECNICO)))
        .and(impiegato.contractor.eq(false))
        .or(
          customizationId
            ? impiegato.personalizzazioni.any((pers: Qpersonalizzazione) => pers.id.eq(Number(customizationId)))
            : null,
        ),
    )
    builder.select('id', 'fullname')
    builder.orderBy(impiegato.fullname.asc())
  })

  const getAnalisi =
    customization.sede || clientId
      ? qsAdminApi.analisi().query((builder, analisi) => {
          if (customization.sede?.azienda?.gruppo_aziendale?.id)
            builder.filter(
              analisi.sede.props.azienda.props.gruppo_aziendale.props.id.eq(
                customization.sede.azienda.gruppo_aziendale.id,
              ),
            )
          else if (azienda?.data.gruppo_aziendale?.id)
            builder.filter(
              analisi.sede.props.azienda.props.gruppo_aziendale.props.id.eq(azienda?.data.gruppo_aziendale?.id),
            )
          else
            builder.filter(
              analisi.sede.props.azienda.props.id.eq(Number(customization.sede?.azienda?.id || azienda?.data.id)),
            )
          builder.expanding('sede', (sedeBuilder) => {
            sedeBuilder.expanding('azienda', (aziendaBuilder, azienda) => {
              aziendaBuilder.select('id', 'nome')
            })
            sedeBuilder.select('id', 'nome', 'azienda')
          })
          builder.select('id', 'ded_Dis', 'oggetto', 'sede')
          builder.orderBy(analisi.ded_Dis.desc())
        })
      : undefined

  return defer({
    title,
    customization,
    getAziende,
    getQsImpiegati,
    getUserInfo: getAzureUserInformation(),
    getAnalisi,
    defaultCRUDAllowedRoles: [Roles.GlobalAdministrator, Roles.TechDeveloper, Roles.Administrator, Roles.Supervisor],
  })
}) satisfies LoaderFunction

export const customizationEditorRouteAction = (async ({ request, params }: RouteFunctionParams) => {
  const customization = (await request.json()) as personalizzazione
  const qsAdminApi = useQsAdminApiManager.getState().service

  switch (request.method) {
    case 'POST': {
      const res = await qsAdminApi.personalizzazione().create(customization)
      return redirect(`../${res.data.id}`)
    }
    case 'PUT':
    case 'PATCH': {
      return await qsAdminApi.personalizzazione(customization.id).update(customization)
    }
    default: {
      throw new Response('Method not allowed', {
        status: 405,
        statusText: 'Method not allowed',
      })
    }
  }
}) satisfies ActionFunction

const CustomizationEditorRoute = {
  path: ':customizationId',
  element: <CustomizationEditor creating={false} />,
  loader: customizationEditorRouteLoader,
  action: customizationEditorRouteAction,
  allowedRoles: [Roles.Guest],
  handle: {
    crumb: (match) => ({
      label: match.data.customization.ded_Dis,
    }),
  },
} as RoleRouteObject

export default CustomizationEditorRoute
